import React, { useState, useEffect } from "react";
import { Table, Button, Form, Card, Row, Col, Modal } from "react-bootstrap";
import { FaPlus, FaEdit, FaCheck, FaCopy, FaTrash, FaFile, FaFileExcel } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { fetchAPI } from "../utils/fetchAPI";
import * as XLSX from "xlsx";
import AddSteelForm from "../components/AddSteelForm";
import EditSteelForm from "../components/EditSteelForm";
import UseSteelModal from "../components/UseSteelModal";
import AddWasteModal from "../components/AddWasteModal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import NavbarTop from "../components/NavbarTop";
import NavbarBottom from "../components/NavbarBottom";
import { WhiteBoxWrapper } from "../components/Login.style";

interface SteelItem {
  id: number;
  name: string;
  steelType: number;
  steelStatus: number;
  width: number;
  height: number;
  thickness: number;
  weight: number;
  grade: string;
  usedTo: string;
  info: string;
}

interface SteelSummary {
  totalSheets: number;
  totalWaste: number;
  byThickness: { [key: number]: { sheets: number; waste: number } };
  byGrade: { [key: string]: { sheets: number; waste: number } };
}

interface FileAttachment {
  id: number;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  upload_date: string;
}

const SteelInventory: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [steelItems, setSteelItems] = useState<SteelItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUseModal, setShowUseModal] = useState(false);
  const [showWasteModal, setShowWasteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SteelItem | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filters, setFilters] = useState({
    grade: searchParams.get("grade") || "",
    thickness: searchParams.get("thickness") || "",
  });
  const [files, setFiles] = useState<FileAttachment[]>([]);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState("");
  const [fileName, setFileName] = useState("");
  const [selectedItemForFiles, setSelectedItemForFiles] = useState<SteelItem | null>(null);

  const updateFilters = (newFilters: { grade: string; thickness: string }) => {
    setFilters(newFilters);
    const params = new URLSearchParams();
    if (newFilters.grade) params.set("grade", newFilters.grade);
    if (newFilters.thickness) params.set("thickness", newFilters.thickness);
    setSearchParams(params);
  };

  const fetchSteelItems = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.grade) queryParams.append("grade", filters.grade);
      if (filters.thickness) queryParams.append("thickness", filters.thickness);

      const data = await fetchAPI("GET", `steel.php?${queryParams.toString()}`);

      if (data && data.success) {
        setSteelItems(data.data);
      } else {
        throw new Error(data?.message || "Nie udało się pobrać danych stali.");
      }
    } catch (error: any) {
      console.error("Error fetching steel items:", error);
      setErrorMessage(error.message || "Wystąpił błąd podczas pobierania danych.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSteelItems();
  }, [filters]);

  const getSteelTypeText = (type: number): string => {
    switch (type) {
      case 1:
        return "Cały arkusz";
      case 2:
        return "Odpad użyteczny";
      case 3:
        return "Wykorzystana";
      default:
        return "Nieznany typ";
    }
  };

  const getSteelStatusText = (status: number, steelType: number): string => {
    if (steelType === 3) return "-";

    switch (status) {
      case 0:
        return "Dostępna";
      case 1:
        return "W użyciu";
      case 2:
        return "Zarezerwowana";
      case 3:
        return "Zniszczona";
      case 4:
        return "Wykorzystana";
      default:
        return "-";
    }
  };

  const handleEditClick = (item: SteelItem) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleUseClick = (item: SteelItem) => {
    setSelectedItem(item);
    setSelectedItemId(item.id);
    setShowUseModal(true);
  };

  const handleWasteClick = (item: SteelItem) => {
    setSelectedItem(item);
    setShowWasteModal(true);
  };

  const handleDeleteClick = (item: SteelItem) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;

    setIsDeleting(true);
    try {
      const response = await fetchAPI("POST", "deleteSteel.php", {
        id: selectedItem.id,
      });

      if (response.success) {
        setSteelItems((prev) => prev.filter((item) => item.id !== selectedItem.id));
        setShowDeleteModal(false);
      } else {
        setErrorMessage(response.message || "Nie udało się usunąć pozycji.");
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Wystąpił błąd podczas usuwania pozycji.");
    } finally {
      setIsDeleting(false);
      setSelectedItem(null);
    }
  };

  const calculateSummary = (items: SteelItem[]): SteelSummary => {
    const summary: SteelSummary = {
      totalSheets: 0,
      totalWaste: 0,
      byThickness: {},
      byGrade: {},
    };

    items.forEach((item) => {
      // Inicjalizacja dla grubości
      if (!summary.byThickness[item.thickness]) {
        summary.byThickness[item.thickness] = { sheets: 0, waste: 0 };
      }

      // Inicjalizacja dla gatunku
      if (!summary.byGrade[item.grade]) {
        summary.byGrade[item.grade] = { sheets: 0, waste: 0 };
      }

      // Aktualizacja liczników
      if (item.steelType === 1) {
        // Cały arkusz
        summary.totalSheets++;
        summary.byThickness[item.thickness].sheets++;
        summary.byGrade[item.grade].sheets++;
      } else if (item.steelType === 2) {
        // Odpad użyteczny
        summary.totalWaste++;
        summary.byThickness[item.thickness].waste++;
        summary.byGrade[item.grade].waste++;
      }
    });

    return summary;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        setUploadError("Plik jest zbyt duży. Maksymalny rozmiar to 10MB.");
        return;
      }
      setSelectedFile(file);
      setFileName(file.name);
      setUploadError("");
    }
  };

  const uploadFile = async () => {
    if (!selectedFile || !selectedItemForFiles || !fileName) return;

    const formData = new FormData();
    formData.append("steel_id", selectedItemForFiles.id.toString());
    formData.append("file", selectedFile);
    formData.append("file_name", fileName);

    try {
      const data = await fetchAPI("POST", "uploadSteelFile.php", formData);
      if (data.success) {
        setShowFileUploadModal(false);
        setSelectedFile(null);
        setFileName("");
        fetchFiles(selectedItemForFiles.id);
      } else {
        setUploadError(data.message || "Wystąpił błąd podczas przesyłania pliku");
      }
    } catch (error) {
      setUploadError("Wystąpił błąd podczas przesyłania pliku");
    }
  };

  const fetchFiles = async (steelId: number) => {
    try {
      const data = await fetchAPI("GET", `getSteelFiles.php?steel_id=${steelId}`);
      if (data.success) {
        setFiles(data.files);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  };

  const handleShowFiles = (item: SteelItem) => {
    setSelectedItemForFiles(item);
    fetchFiles(item.id);
  };

  const generateExcel = () => {
    // Przygotuj dane do eksportu
    const exportData = steelItems.map((item) => ({
      Nazwa: item.name,
      Gatunek: item.grade,
      Grubość: `${item.thickness}mm`,
      Wymiary: `${item.width}x${item.height}mm`,
      Waga: `${item.weight}kg`,
      Status: getSteelStatusText(item.steelStatus, item.steelType),
      Typ: getSteelTypeText(item.steelType),
      "Wykorzystano do": item.usedTo || "-",
      Informacje: item.info || "-",
    }));

    // Utwórz nowy arkusz
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Magazyn Stali");

    // Zapisz plik
    XLSX.writeFile(wb, "magazyn_stali.xlsx");
  };

  return (
    <div>
      <NavbarTop />
      <div>
        <WhiteBoxWrapper>
          <div className="row align-items-center">
            <div className="col">
              <h2>
                <b>Magazyn stali</b>
              </h2>
            </div>
            <div className="col-auto">
              <Button variant="primary" onClick={() => setShowAddModal(true)}>
                <FaPlus /> Dodaj nową pozycję
              </Button>
            </div>
          </div>
          <Row>
            <Col xs={12} className="d-flex justify-content-end mt-3">
              <Button variant="primary" onClick={generateExcel}>
                <FaFileExcel /> Generuj Excel
              </Button>
            </Col>
          </Row>
        </WhiteBoxWrapper>

        <Card className="mb-4">
          <Card.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Gatunek</Form.Label>
                    <Form.Select
                      value={filters.grade}
                      onChange={(e) => updateFilters({ ...filters, grade: e.target.value })}
                    >
                      <option value="">Wszystkie gatunki</option>
                      {Array.from(new Set(steelItems.map((item) => item.grade))).map((grade) => (
                        <option key={grade} value={grade}>
                          {grade}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Grubość (mm)</Form.Label>
                    <Form.Select
                      value={filters.thickness}
                      onChange={(e) => updateFilters({ ...filters, thickness: e.target.value })}
                    >
                      <option value="">Wszystkie grubości</option>
                      {Array.from(new Set(steelItems.map((item) => item.thickness)))
                        .sort((a, b) => a - b)
                        .map((thickness) => (
                          <option key={thickness} value={thickness}>
                            {thickness}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {loading ? (
          <div>Ładowanie...</div>
        ) : errorMessage ? (
          <div className="text-red-500">{errorMessage}</div>
        ) : (
          <>
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title mb-4">Podsumowanie</h5>
                <div className="row">
                  <div className="col-md-4">
                    <div className="card bg-light">
                      <div className="card-body">
                        <h6 className="card-subtitle mb-3">Ogólne podsumowanie</h6>
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Typ</th>
                                <th>Ilość</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Całe arkusze</td>
                                <td>{calculateSummary(steelItems).totalSheets}</td>
                              </tr>
                              <tr>
                                <td>Odpady</td>
                                <td>{calculateSummary(steelItems).totalWaste}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-light">
                      <div className="card-body">
                        <h6 className="card-subtitle mb-3">Według grubości</h6>
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Grubość</th>
                                <th>Arkusze</th>
                                <th>Odpady</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(calculateSummary(steelItems).byThickness)
                                .sort(([a], [b]) => Number(a) - Number(b))
                                .map(([thickness, counts]) => (
                                  <tr key={thickness}>
                                    <td>{thickness}mm</td>
                                    <td>{counts.sheets}</td>
                                    <td>{counts.waste}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-light">
                      <div className="card-body">
                        <h6 className="card-subtitle mb-3">Według gatunku</h6>
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Gatunek</th>
                                <th>Arkusze</th>
                                <th>Odpady</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(calculateSummary(steelItems).byGrade)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([grade, counts]) => (
                                  <tr key={grade}>
                                    <td>{grade}</td>
                                    <td>{counts.sheets}</td>
                                    <td>{counts.waste}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <div className="row">
                {steelItems.map((item) => (
                  <div className="col-12 col-md-6 col-lg-4 mb-4 d-flex" key={item.id}>
                    <Card
                      className={`p-4 flex-grow-1 ${
                        item.steelType === 1
                          ? "bg-success bg-opacity-10"
                          : item.steelType === 2
                            ? "bg-warning bg-opacity-10"
                            : "bg-secondary bg-opacity-10"
                      }`}
                      style={{
                        border: item.steelType === 2 ? "2px solid #f4d457" : "1px solid #dee2e6",
                        borderRadius: "8px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: item.steelType === 3 ? "#f8f9fa" : undefined,
                      }}
                    >
                      <div className="flex justify-between items-start mb-4 border-b pb-3">
                        <div>
                          <h3 className="text-xl font-bold mb-1">{item.name}</h3>
                          <div className=" text-gray-600">
                            <span className="font-semibold">Gatunek:</span> <b>{item.grade}</b>
                          </div>
                        </div>
                      </div>
                      <div className="w-100">
                        <table
                          className="table table-borderless"
                          style={{ "--bs-table-bg": "transparent" } as React.CSSProperties}
                        >
                          <tbody>
                            <tr>
                              <td
                                className="text-sm font-semibold text-gray-600"
                                style={{ padding: "0 8px 0 0", minWidth: 110 }}
                              >
                                Grubość:
                              </td>
                              <td className="text-base" style={{ padding: 0 }}>
                                <b>{item.thickness}mm</b>
                              </td>
                            </tr>

                            <tr>
                              <td
                                className="text-sm font-semibold text-gray-600"
                                style={{ padding: "0 8px 0 0", minWidth: 110 }}
                              >
                                Wymiary:
                              </td>
                              <td className="text-base" style={{ padding: 0 }}>
                                <b>
                                  {item.width}x{item.height}mm
                                </b>
                              </td>
                            </tr>

                            <tr>
                              <td
                                className="text-sm font-semibold text-gray-600"
                                style={{ padding: "0 8px 0 0", minWidth: 110 }}
                              >
                                Waga:
                              </td>
                              <td className="text-base font-bold" style={{ padding: 0 }}>
                                {item.weight}kg
                              </td>
                            </tr>
                            <tr>
                              <td
                                className="text-sm font-semibold text-gray-600"
                                style={{ padding: "0 8px 0 0", minWidth: 110 }}
                              >
                                Status:
                              </td>
                              <td className="text-base" style={{ padding: 0 }}>
                                <b>{getSteelStatusText(item.steelStatus, item.steelType)}</b>
                              </td>
                            </tr>
                            <tr>
                              <td
                                className="text-sm font-semibold text-gray-600"
                                style={{ padding: "0 8px 0 0", minWidth: 110 }}
                              >
                                Typ:
                              </td>
                              <td className="text-base" style={{ padding: 0 }}>
                                <b className={item.steelType === 3 ? "text-secondary" : ""}>
                                  {getSteelTypeText(item.steelType)}
                                </b>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      {item.steelType !== 3 && (
                        <div className="row mt-4">
                          <div className="col-6">
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleUseClick(item)}
                              className="action-button w-100 bg-white"
                              style={{ padding: "0.25rem 0.75rem" }}
                            >
                              <FaCheck /> ZUŻYJ
                            </Button>
                          </div>
                          <div className="col-6">
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleWasteClick(item)}
                              className="action-button w-100 bg-white"
                              style={{ padding: "0.25rem 0.75rem" }}
                            >
                              <FaCopy /> DODAJ ODPAD
                            </Button>
                          </div>
                        </div>
                      )}
                      załączniki
                      {(item.usedTo || item.info) && (
                        <div className="mt-4 pt-3 border-t">
                          <table
                            className="table table-borderless"
                            style={{ "--bs-table-bg": "transparent", padding: 0 } as React.CSSProperties}
                          >
                            <tbody>
                              {item.usedTo && (
                                <tr>
                                  <td
                                    className="text-sm font-semibold text-gray-600"
                                    style={{ padding: "0 8px 0 0", minWidth: 110 }}
                                  >
                                    Wykorzystano do:
                                    <br /> {item.usedTo}
                                  </td>
                                </tr>
                              )}
                              {item.info && (
                                <tr>
                                  <td
                                    className="text-sm font-semibold text-gray-600"
                                    style={{ padding: "0 8px 0 0", minWidth: 110 }}
                                  >
                                    Informacje:
                                    <br /> {item.info}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {item.steelType !== 3 && (
                        <div className="flex flex-col w-full mt-4">
                          <div className="flex gap-3">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditClick(item)}
                              className="action-button flex-1"
                              style={{ padding: "0.25rem 0.75rem" }}
                            >
                              <FaEdit /> Edytuj
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteClick(item)}
                              className="action-button flex-1"
                              style={{ padding: "0.25rem 0.75rem" }}
                            >
                              <FaTrash /> Usuń
                            </Button>
                          </div>
                        </div>
                      )}
                      <div className="mt-4">
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleShowFiles(item)}
                          className="w-100"
                        >
                          <FaFile /> Załączniki
                        </Button>
                      </div>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <AddSteelForm show={showAddModal} onHide={() => setShowAddModal(false)} onSuccess={fetchSteelItems} />

        {selectedItem && (
          <>
            <EditSteelForm
              show={showEditModal}
              onHide={() => {
                setShowEditModal(false);
                setSelectedItem(null);
              }}
              onSuccess={fetchSteelItems}
              item={selectedItem}
            />
            <UseSteelModal
              show={showUseModal}
              onHide={() => {
                setShowUseModal(false);
                setSelectedItem(null);
                setSelectedItemId(null);
              }}
              onSuccess={fetchSteelItems}
              itemId={selectedItem.id}
              item={selectedItem}
            />
            <AddWasteModal
              show={showWasteModal}
              onHide={() => {
                setShowWasteModal(false);
                setSelectedItem(null);
              }}
              onSuccess={fetchSteelItems}
              parentItem={selectedItem}
            />
          </>
        )}

        <DeleteConfirmationModal
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedItem(null);
          }}
          onConfirm={handleDeleteConfirm}
          itemName={selectedItem?.name || ""}
          isLoading={isDeleting}
        />

        {/* File Upload Modal */}
        <Modal show={showFileUploadModal} onHide={() => setShowFileUploadModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Dodaj załącznik</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="fileUpload">
              <Form.Label>Wybierz plik (PDF, JPG, PNG)</Form.Label>
              <Form.Control type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} />
            </Form.Group>
            <Form.Group controlId="fileName" className="mt-3">
              <Form.Label>Nazwa załącznika</Form.Label>
              <Form.Control
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Wprowadź nazwę załącznika"
              />
            </Form.Group>
            {uploadError && <div className="text-danger mt-2">{uploadError}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowFileUploadModal(false)}>
              Anuluj
            </Button>
            <Button variant="primary" onClick={uploadFile} disabled={!selectedFile || !fileName}>
              Prześlij
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Files Modal */}
        <Modal show={!!selectedItemForFiles} onHide={() => setSelectedItemForFiles(null)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Załączniki - {selectedItemForFiles?.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Button variant="primary" onClick={() => setShowFileUploadModal(true)} className="mb-3">
              <FaPlus /> Dodaj załącznik
            </Button>

            {files.length > 0 ? (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nazwa pliku</th>
                      <th>Typ</th>
                      <th>Rozmiar</th>
                      <th>Data dodania</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((file) => (
                      <tr key={file.id}>
                        <td>{file.file_name}</td>
                        <td>{file.file_type}</td>
                        <td>{(file.file_size / 1024).toFixed(2)} KB</td>
                        <td>{new Date(file.upload_date).toLocaleString()}</td>
                        <td>
                          <a
                            href={file.file_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-primary"
                          >
                            Pobierz
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center">Brak załączników.</p>
            )}
          </Modal.Body>
        </Modal>
      </div>
      <NavbarBottom />
    </div>
  );
};

export default SteelInventory;
